// src/pages/products/litigation-management.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRight, 
  Gavel, 
    Cloud,
    Link,
    BarChart3, 
  FileWarning, 
  ShieldCheck, 
  Bell, 
  DollarSign, 
  LayoutDashboard, 
  Database 
} from 'lucide-react';

const LitigationModule = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imageY = useTransform(scrollY, [0, 1000], [0, -120]); // strong parallax
  const badgeY = useTransform(scrollY, [0, 600], [0, -50]);

  const handleFreeConsultation = () => navigate('/contact');

  const featureSet = [
    { icon: Cloud, title: "Cloud-based case file access", desc: "From any authorised device — secure & instant" },
    { icon: ShieldCheck, title: "AES-256 encryption", desc: "Complete confidentiality of all legal records" },
    { icon: Database, title: "Real-time data synchronisation", desc: "Across legal teams and compliance managers" },
    { icon: FileWarning, title: "Notice tagging & categorisation", desc: "With historical document indexing" },
    { icon: Bell, title: "Automated deadline tracking", desc: "For responses to notices and summons" },
    { icon: DollarSign, title: "Expense tracking", desc: "Penalties, legal fees, and compliance costs" },
    { icon: LayoutDashboard, title: "Case status dashboard", desc: "Track progress from receipt to closure" },
    { icon: Link, title: "Integration with external legal counsel portals", desc: "Roadmap feature" },
    { icon: BarChart3, title: "Reporting", desc: "Pending notices, overdue responses, expenditure summaries" }
  ];

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 overflow-hidden">
      {/* Hero – ultra-premium with enhanced animations */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-emerald-50 via-white to-cyan-50 pt-24 overflow-hidden">
        {/* Multiple animated orbs for richer depth */}
        <motion.div
          animate={{ scale: [1, 1.22, 1], rotate: [0, 8, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-80 -left-80 w-[1200px] h-[1200px] bg-emerald-200/25 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.28, 1], rotate: [0, -10, 0] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -bottom-80 -right-80 w-[1300px] h-[1300px] bg-cyan-200/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [-30, 30, -30], opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 14, repeat: Infinity }}
          className="absolute top-1/4 left-1/3 w-[500px] h-[500px] bg-emerald-300/15 rounded-full blur-2xl"
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              style={{ y: badgeY }}
              className="inline-flex items-center gap-5 bg-white/90 backdrop-blur-2xl border border-emerald-100/60 px-9 py-5 rounded-full mb-12 shadow-2xl"
            >
              <Gavel className="text-[#d97925]" size={32} />
              <span className="text-[#d97925] font-bold tracking-[0.25em] uppercase text-base">
                Litigation Management Module
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 80 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.2 }}
              className="text-6xl md:text-[110px] leading-[0.92] font-black tracking-[-0.05em] mb-12 bg-gradient-to-r from-[#d97925] via-[#d97925] to-[#d97925] bg-clip-text text-transparent"
            >
              Litigation Management Module
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 70 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.4 }}
              className="text-3xl md:text-4xl font-semibold text-zinc-800 max-w-4xl leading-tight"
            >
              Legal Compliance Tracking — Organised, Encrypted, Accessible
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 60 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.6 }}
              className="text-2xl md:text-3xl text-zinc-600 mt-8 max-w-4xl leading-tight"
            >
              Purpose-built legal compliance tracking tool for organisations managing notices, summons, legal proceedings, and case histories related to statutory compliance.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-32 md:py-40 bg-white relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 xl:gap-24">
            {/* Left – Text & Features */}
            <div className="lg:col-span-7 space-y-24 lg:space-y-32">
              {/* Feature Set */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-12 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  Litigation Module — Feature Set
                </h3>

                <div className="grid md:grid-cols-2 gap-8">
                  {featureSet.map((feature, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 60, scale: 0.92 }}
                      whileInView={{ opacity: 1, y: 0, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07, type: "spring", stiffness: 90 }}
                      whileHover={{ y: -12, scale: 1.03, boxShadow: "0 20px 40px -10px rgba(16,185,129,0.25)" }}
                      className="bg-white border border-emerald-100/60 rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:border-emerald-300 transition-all duration-400 group"
                    >
                      <div className="flex items-start gap-5">
                        <div className="mt-1 w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-100 transition-colors">
                          <feature.icon className="text-[#d97925]" size={26} />
                        </div>
                        <div>
                          <h4 className="text-xl font-semibold text-[#d97925] group-hover:text-[#d97925] transition-colors">
                            {feature.title}
                          </h4>
                          <p className="text-[17px] leading-relaxed text-zinc-700 mt-2">
                            {feature.desc}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Who Benefits */}
              <motion.div
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-150px" }}
                transition={{ duration: 1, delay: 0.4 }}
              >
                <h3 className="text-4xl md:text-5xl font-bold tracking-tight mb-8 bg-gradient-to-r from-[#d97925] to-[#d97925] bg-clip-text text-transparent">
                  Who Benefits
                </h3>
                <p className="text-xl md:text-2xl leading-relaxed text-zinc-800">
                  HR teams, legal and compliance departments, CFOs tracking compliance costs, and General Counsels overseeing regulatory litigation.
                </p>
              </motion.div>
            </div>

            {/* Right – Visuals with parallax */}
            <motion.div
              style={{ y: imageY }}
              className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-12"
            >
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-zinc-200/80">
                <img
                  src=".././Assets/Images/Litigation.jpg"
                  alt="Litigation Management Dashboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-10 left-10 text-white">
                  <p className="text-2xl font-bold drop-shadow-lg">Organised legal compliance tracking</p>
                  <p className="text-base opacity-90 mt-2 drop-shadow">Powered by TickBox Litigation Module</p>
                </div>
              </div>

              {/* Deadline Tracking Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-3xl p-10 border border-emerald-100 shadow-2xl"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-md">
                    <Bell className="text-[#d97925]" size={40} />
                  </div>
                  <h4 className="text-3xl font-bold text-[#d97925]">Automated Deadline Tracking</h4>
                </div>
                <p className="text-lg text-zinc-800 leading-relaxed">
                  Never miss a response deadline — automated tracking for notices, summons, and legal timelines.
                </p>
              </motion.div>

              {/* Case Dashboard Card */}
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white rounded-3xl p-10 border border-emerald-100 shadow-xl flex items-start gap-6"
              >
                <LayoutDashboard className="text-[#d97925] mt-1" size={48} />
                <div>
                  <h4 className="text-3xl font-bold mb-4 text-[#d97925]">Case Status Dashboard</h4>
                  <p className="text-lg text-zinc-700">
                    Track every case from receipt to closure — real-time progress for legal & compliance teams.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1 }}
            className="mt-32 md:mt-40 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white rounded-3xl py-24 px-8 md:px-16 text-center shadow-2xl relative overflow-hidden"
          >
            {/* Animated shine overlay */}
            <motion.div
              animate={{ x: ["-100%", "100%"] }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-40"
            />

            <h3 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 relative z-10">
              Ready to organise your legal compliance?
            </h3>
            <p className="text-2xl md:text-3xl mb-12 max-w-4xl mx-auto opacity-90 relative z-10">
              Track notices, summons, proceedings, and costs — encrypted, accessible, and fully organised.
            </p>

            <motion.button
              whileHover={{ scale: 1.06, boxShadow: "0 30px 60px -15px rgba(16, 185, 129, 0.5)" }}
              whileTap={{ scale: 0.98 }}
              onClick={handleFreeConsultation}
              className="inline-flex items-center gap-5 px-16 py-8 bg-white text-[#d97925] font-bold text-2xl md:text-3xl rounded-3xl shadow-2xl hover:bg-emerald-50 transition-all relative z-10"
            >
              Request Litigation Module Access
              <ArrowRight size={36} />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LitigationModule;